import React, { useState, useEffect, useContext } from 'react';
import { stripHtml } from 'string-strip-html';
import Button from '../../components/@base/Button';
import Input from '../../components/@base/Input';
import TextArea from '../../components/@base/TextArea';
import ContextMenu from '../../components/ContextMenu/ContextMenu';
import TagInput from '../../components/TagsInput/TagsInput';
import ChatbotTestingContext from '../../context/ChatbotTestingContext';
import FlowContext from '../../context/FlowContext';

import {
  ExpectedResultDTO, TestCaseStatus, TextCaseType,
} from '../../dto/TestCaseDTO';
import gatewayEndPoint from '../../service/Gateway';
import ChatbotTestingStatus from './ChatbotTestingStatus';
import MessageNotification from '../../components/MessageNotification/MessageNotification';

import styles from './ChatbotTesting.module.css';
import DialogNotification from '../../components/DialogNotification/DialogNotification';
import ConversationFlowBreadCrumbsWithFlowConfig from '../ConversationFlow/ConversationFlowBreadCrumbsWithFlowConfig';
import ChatbotTestingSuites from './ChatbotTestingSuites';

const checkAnswer = (expected, real) => {
  if (expected.length !== real.length) return false;

  for (let index = 0; index < expected.length; index++) {
    const expectedPart = expected[index];
    const realPart = real[index];

    if (expectedPart.type !== TextCaseType[realPart.type]) return false;

    const realPartText = (realPart.text ? realPart.text : realPart.title) || '';
    const expectedPartText = expectedPart.text || '';

    if (expectedPart.text === '' && realPartText !== '') return false;
    if (expectedPart.text === '' && realPartText !== '') return false;

    let a = stripHtml(expectedPartText).result.replace(/(\r\n|\n|\r|\s)/gm, '');
    let b = stripHtml(realPartText).result.replace(/(\r\n|\n|\r|\s)/gm, '');

    if (a !== b) return false;

    // A partir dessa linha já se sabe que o texto ou titulo são iguais então
    // Se o tipo do conteudo for lista de opções a comparação continua a seguir
    if (expectedPart.type === TextCaseType.OPTIONS) {
      if (expectedPart.options != null && realPart.options == null) return false;
      if (expectedPart.options == null && realPart.options != null) return false;

      if (expectedPart.options.length !== realPart.options.length) return false;

      a = stripHtml(expectedPart.options.join(',')).result.replace(/(\r\n|\n|\r|\s)/gm, '');
      b = stripHtml(realPart.options.map((o) => o.label).join(',')).result.replace(/(\r\n|\n|\r|\s)/gm, '');

      return a === b;
    }
  }

  return true;
};

export function ChatbotTesting(props) {
  const chatbotTestingContext = useContext(ChatbotTestingContext);
  const flowContext = useContext(FlowContext);

  const [testCases, setTestCases] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [showError, setShowError] = useState(false);

  const handleChangeSendMessage = (idxTestCaseList, value) => {
    testCases[idxTestCaseList].message = value;
    setTestCases([...testCases]);
  };

  const handleChangeExpectedMessages = (idxTestCaseList, idxExpecteMessageList, attr, value) => {
    testCases[idxTestCaseList].expectedResult[idxExpecteMessageList][attr] = value;
    setTestCases([...testCases]);

    if (attr === 'options') {
      chatbotTestingContext.updateTestCase(testCases[idxTestCaseList]);
    }
  };

  const handleOnBlur = (idxTestCaseList) => {
    chatbotTestingContext.updateTestCase(testCases[idxTestCaseList]);
  };

  const handleMoreExpectedMessage = (idxTestCaseList, type) => {
    const testCase = testCases[idxTestCaseList];
    testCase.expectedResult.push(new ExpectedResultDTO(type));
    chatbotTestingContext.updateTestCase(testCases[idxTestCaseList]);
  };

  const handleRemoveExpectedMessage = (idxTestCaseList, idxExpecteMessageList) => {
    const testCase = testCases[idxTestCaseList];
    testCase.expectedResult.splice(idxExpecteMessageList, 1);
    chatbotTestingContext.updateTestCase(testCases[idxTestCaseList]);
  };

  async function getConversartionId(publication) {
    let conveId = conversationId;
    if (conveId === null) {
      try {
        const response = await gatewayEndPoint.post(`${publication}api/conversation/`);
        if (response.status === 200) {
          conveId = response.data;
          setConversationId(conveId);
        }
      } catch (err) {
        setShowError(true);
        conveId = null;
        setConversationId(null);
      }
    }
    return conveId;
  }

  async function sendMessage(conveId, publication, testCase) {
    const payload = {
      conversationId: conveId,
      text: testCase.message,
    };
    try {
      return await gatewayEndPoint.post(`${publication}api/answer/`, payload);
    } catch (e) {
      return e.response;
    }
  }

  const handleExecuteAllTests = () => {
    const testCaseUpdated = [...testCases];
    testCaseUpdated.forEach((t) => { t.status = TestCaseStatus.IN_PROGRESS; });
    setConversationId(null);
    setTestCases([...testCaseUpdated]);
  };

  const handleExecuteTest = (idxTestCaseList) => {
    const testCaseUpdated = [...testCases];
    testCaseUpdated[idxTestCaseList].status = TestCaseStatus.IN_PROGRESS;
    setTestCases([...testCaseUpdated]);
  };

  const handleMoveUp = (idxTestCaseList) => {
    const current = testCases[idxTestCaseList];
    const previous = testCases[idxTestCaseList - 1];
    current.previous = previous.previous;
    chatbotTestingContext.updateTestCase(current, chatbotTestingContext.fetchTestCases);
  };

  const handleMoveDown = (idxTestCaseList) => {
    const current = testCases[idxTestCaseList];
    const next = testCases[idxTestCaseList + 1];
    next.previous = current.previous;
    chatbotTestingContext.updateTestCase(next, chatbotTestingContext.fetchTestCases);
  };

  async function executeTest(execution) {
    const testCasesUpdated = [...testCases];

    const publication = flowContext.lastPublication.uiWebReadyURL;
    let conveId = await getConversartionId(publication);
    if (conveId == null) {
      return testCasesUpdated.map((t) => { t.status = TestCaseStatus.ERROR; return t; });
    }

    for (let index = 0; index < execution.length;) {
      const exec = execution[index];
      const response = await sendMessage(conveId, publication, exec);
      if (response.status === 400) {
        index = 0;
        conveId = await getConversartionId(publication);
      } else if (response.status === 200) {
        const idx = testCasesUpdated.findIndex((t) => t.id === exec.id);

        testCasesUpdated[idx].result = response.data.contents;
        testCasesUpdated[idx].status = checkAnswer(exec.expectedResult, response.data.contents)
          ? TestCaseStatus.SUCCESS : TestCaseStatus.FAIL;

        index += 1;
      }
    }

    return testCasesUpdated;
  }

  useEffect(() => {
    const execution = testCases.filter((t) => t.status === TestCaseStatus.IN_PROGRESS);
    if (execution && execution.length > 0) {
      executeTest(execution).then((testCasesUpdated) => setTestCases([...testCasesUpdated]));
    }
  }, [testCases]);

  useEffect(() => {
    if (flowContext.lastPublication) {
      props.setBreadscrumbs(null);
      props.setBreadscrumbs(
        <ConversationFlowBreadCrumbsWithFlowConfig
          contextBots={flowContext}
        />,
      );
      chatbotTestingContext.fetchTestCasesSuites();
    }
  }, [flowContext.lastPublication]);

  useEffect(() => {
    chatbotTestingContext.fetchTestCases();
  }, [chatbotTestingContext.testCasesSuites]);

  useEffect(() => {
    if (chatbotTestingContext.testCases.length > 0
      && chatbotTestingContext.testCasesSuites.length === 0) {
      chatbotTestingContext.fetchTestCasesSuites();
    } else {
      setTestCases(chatbotTestingContext.getTestCasesCopy());
    }
  }, [chatbotTestingContext.testCases]);

  if (!flowContext.lastPublication
    || flowContext.flow.environment.currentDeploy.flow.id !== flowContext.flow.id) {
    return (
      <MessageNotification
        content="Esse fluxo não pode ser testado! Antes de testar o fluxo, você deve publicá-lo."
        type="danger"
        hideClose
      />
    );
  }

  return (
    <div style={{ height: '100%', overflow: 'hidden' }}>
      {showError ? (
        <DialogNotification
          okAction={() => setShowError(false)}
          title="Erro!"
          content={(
            <p>
              Aconteceu um erro inesperado na execução dos testes.
              <br />
              Entre em contato com o suporte do Serprobots.
            </p>
          )}
        />
      ) : null}
      {testCases && testCases.length > 0 ? (
        <>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 30, paddingBottom: 16,
          }}
          >
            <ChatbotTestingSuites />
            <div className={styles.Actions}>
              {testCases && testCases.length > 0 ? (
                <Button
                  primary
                  small
                  onClick={handleExecuteAllTests}
                >
                  <small>Executar todos os testes</small>
                </Button>
              ) : null}
              <Button
                primary
                small
                onClick={chatbotTestingContext.createTestCase}
              >
                <small>Adicionar novo teste</small>
              </Button>
              <div className="clearfix" />
            </div>
          </div>

          <div className={styles.Header}>
            <div />
            <div>Mensagem Enviada</div>
            <div>Mensagens Esperadas</div>
            <div>Resultado</div>
            <div>Ações</div>
          </div>
          <div className={styles.List}>
            {
              testCases.map((testCase, idx) => (
                <div key={idx}>
                  <div>{idx + 1}</div>
                  <div>
                    <TextArea
                      value={testCase.message}
                      onChange={(v) => handleChangeSendMessage(idx, v.target.value)}
                      onBlur={() => handleOnBlur(idx)}
                    />
                  </div>
                  <div className={styles.ExpectedMessages}>
                    {
                      testCase.expectedResult.map((result, idxResult) => {
                        const expectMessageAction = (
                          <>
                            <div>
                              <Button
                                circle
                                tertiary
                                small
                                disabled={testCase.expectedResult.length === 1}
                                onClick={() => { handleRemoveExpectedMessage(idx, idxResult); }}
                              >
                                <i className="fa fa-times" aria-hidden="true" />
                              </Button>

                              <ContextMenu
                                button={(
                                  <Button circle tertiary small>
                                    <i className="fa fa-plus" aria-hidden="true" />
                                  </Button>
                                )}
                                options={[TextCaseType.OPTIONS, TextCaseType.TEXT]
                                  .map((i) => (
                                    <a
                                      key={testCase.id + i}
                                      role="button"
                                      tabIndex={0}
                                      onKeyPress={() => { }}
                                      onClick={() => { handleMoreExpectedMessage(idx, i); }}
                                    >
                                      {i}
                                    </a>
                                  ))}
                              />
                            </div>
                          </>
                        );

                        if (result.type === TextCaseType.TEXT) {
                          return (
                            <div key={idxResult}>
                              <div>
                                <TextArea
                                  onChange={(v) => handleChangeExpectedMessages(idx, idxResult, 'text', v.target.value)}
                                  value={result.text}
                                  onBlur={() => handleOnBlur(idx)}
                                />
                              </div>
                              {expectMessageAction}
                            </div>
                          );
                        }

                        return (
                          <div key={result.id}>
                            <div>
                              <Input
                                label="Titulo"
                                value={result.text}
                                onChange={(v) => handleChangeExpectedMessages(idx, idxResult, 'text', v.target.value)}
                                onBlur={() => handleOnBlur(idx)}
                                inline
                                labelWidth={55}
                              />
                              <div className="mt-1" />
                              <TagInput
                                label="Opções"
                                labelWidth={55}
                                inline
                                value={result.options.map((o) => ({ id: o, text: o }))}
                                onChange={(v) => handleChangeExpectedMessages(idx, idxResult, 'options', v.map((i) => (i.text)))}
                                placeholder="Digite uma opção"
                                delimiters={[9, 13]}
                              />
                            </div>
                            {expectMessageAction}
                          </div>
                        );
                      })

                    }

                  </div>
                  <div>
                    <ChatbotTestingStatus
                      idx={idx}
                      testCase={testCase}
                    />
                  </div>
                  <div>
                    <Button
                      circle
                      primary
                      small
                      title="Executar este teste"
                      onClick={() => handleExecuteTest(idx)}
                    >
                      <i className="fa fa-play fa-sm" aria-hidden="true" />
                    </Button>
                    <Button
                      circle
                      primary
                      small
                      title="Remover este teste"
                      onClick={() => chatbotTestingContext.removeTestCase(testCase)}
                    >
                      <i className="fa fa-times" aria-hidden="true" />
                    </Button>
                    <Button
                      disabled={idx === testCases.length - 1}
                      circle
                      primary
                      small
                      title="Mover teste para baixo"
                      onClick={() => handleMoveDown(idx)}
                    >
                      <i className="fa fa-arrow-down" aria-hidden="true" />
                    </Button>
                    <Button
                      disabled={idx === 0}
                      circle
                      primary
                      small
                      title="Mover teste para cima"
                      onClick={() => handleMoveUp(idx)}
                    >
                      <i className="fa fa-arrow-up" aria-hidden="true" />
                    </Button>
                  </div>
                </div>
              ))
            }
          </div>
        </>
      ) : (
        <div className={styles.WithoutTests}>
          <Button
            primary
            small
            onClick={chatbotTestingContext.createTestCase}
          >
            <small>Adicionar novo teste</small>
          </Button>
        </div>
      )}

    </div>
  );
}

export default ChatbotTesting;
